import React, { useEffect, useRef, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight, LayoutGrid, List, Plus } from 'lucide-react';
import Button from '@shared/ui/Button';
import { EditIcon } from '../../../icons';
import WorkspaceEventModal from './WorkspaceEventModal';
import type { CalendarEvent } from '../../../../types/calendar';
import DatePickerPopover from '../../../calendar/DatePickerPopover';
import toast from 'react-hot-toast';
import api from '../../../../lib/api';
import { WorkspaceTabProps } from '../../../../types/workspace';
import { useCalendarSimple as useCalendar } from './hooks/useCalendarSimple';
import type { CalendarEventSource } from '../../../../types/calendar';
import { deriveDefaultScope, getStoredCalendarScope, setStoredCalendarScope } from '../../../../utils/calendarPrefs';
import { HelpTrigger } from '../../../../help';
import CalendarDayView from '../../../calendar/CalendarDayView';
import ShareCalendarDropdown from '../../../ShareCalendarDropdown';
import HorizontalDateStrip from '../../../calendar/HorizontalDateStrip';
import CalendarAgendaView from '../../../calendar/CalendarAgendaView';
import SearchField from '../../../SearchField';
import { normalizeCalendarDisplayMode } from '../../../../utils/calendarDisplayMode';
import { toLocalDateString } from '../../../../utils/dateUtils';
import { useMobile } from '../../../../hooks/useMediaQuery';
import LoadingSpinner from '@shared/ui/LoadingSpinner';
import ExportMenu from '../../../ExportMenu';
import ViewToggle from '../../../ViewToggle';
import SegmentedControl from '../../../SegmentedControl';

type ViewMode = 'month' | 'week' | 'day';
type DisplayMode = 'cards' | 'table';

// Support both workspace context (filtered) and general context (all events)
interface CalendarTabProps {
  workspaceId?: string;
  /**
   * Suppresses the in-body heading. Set when this renders AS a page, where the
   * global header already carries the title — otherwise the name prints twice.
   * Inside a workspace tab the header shows the workspace, so the heading stays.
   */
  hideHeading?: boolean;
  workspace?: WorkspaceTabProps['workspace'];
  /**
   * Event sources this calendar composes (CALENDAR_SEPARATION_PRD §5.1).
   * Pass a module-level constant so hook identities stay stable. Absent =
   * today's defaults (workspace tab → legislative+workspace).
   */
  sources?: CalendarEventSource[];
}

const CalendarTab: React.FC<CalendarTabProps> = ({ 
  workspaceId,
  hideHeading = false,
  workspace,
  sources
}) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Export state
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [chamberFilter, setChamberFilter] = useState<'all' | 'senate' | 'house' | 'joint'>('all');
  
  // My Events / All Events filter (only used on the general calendars, not
  // workspace-scoped). Precedence (CALENDAR_SEPARATION_PRD §5.7): explicit URL
  // param (deep links) > the shared stored choice (carried from the dashboard
  // panel) > smart default derived from the All-Events window once it loads.
  const urlFilter = !workspaceId ? searchParams.get('filter') : null;
  const storedScope = !workspaceId ? getStoredCalendarScope() : null;
  const [derivedScope, setDerivedScope] = useState<'my' | 'all' | null>(null);
  const eventFilter = workspaceId
    ? 'all_events'
    : urlFilter
      ? urlFilter
      : storedScope
        ? (storedScope === 'my' ? 'my_events' : 'all_events')
        : derivedScope === 'my'
          ? 'my_events'
          : 'all_events';
  const isMyEvents = eventFilter === 'my_events';

  const setEventFilter = (filter: 'my_events' | 'all_events') => {
    // An explicit click is the shared choice — it carries to the dashboard
    // panel and the other calendar page (§5.7).
    setStoredCalendarScope(filter === 'my_events' ? 'my' : 'all');
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('filter', filter);
    setSearchParams(newSearchParams);
  };
  
  // Simple URL-based state management - no useState for dates
  const today = new Date();
  const currentYear = parseInt(searchParams.get('year') || today.getFullYear().toString());
  const currentMonth = parseInt(searchParams.get('month') || (today.getMonth() + 1).toString());
  const currentDay = parseInt(searchParams.get('day') || today.getDate().toString());
  const viewMode = (searchParams.get('view') as ViewMode) || 'month';
  const displayParam = normalizeCalendarDisplayMode(searchParams.get('display')) as DisplayMode | null;
  // Default to table only when not specified
  const displayMode: DisplayMode = displayParam || 'table';
  
  // Mobile detection - force cards view on mobile
  const isMobile = useMobile();
  // Force cards view on mobile - tables are not suitable for narrow screens
  const effectiveDisplayMode: DisplayMode = isMobile ? 'cards' : displayMode;
  
  const currentDate = useMemo(() => new Date(currentYear, currentMonth - 1, currentDay), [currentYear, currentMonth, currentDay]);
  const organizationId = (workspace as any)?.owner_org_id as string | undefined;

  // Rolling window start/end dates anchored from currentDate
  const rangeStartDate = useMemo(() => toLocalDateString(currentDate), [currentDate]);

  const rangeEndDate = useMemo(() => {
    const d = new Date(currentDate);
    if (viewMode === 'month') {
      d.setDate(d.getDate() + 29);
    } else if (viewMode === 'week') {
      d.setDate(d.getDate() + 6);
    }
    return toLocalDateString(d);
  }, [currentDate, viewMode]);

  // Close export menu on outside click
  
  const setDisplayMode = (mode: DisplayMode) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('display', mode);
    setSearchParams(newSearchParams);
  };

  // Export handler for day/week/month views
  // Uses unified POST /api/calendar/export endpoint for all formats and durations
  const handleExport = async (format: 'excel' | 'csv' | 'pdf' | 'word') => {
    try {
      const dayDate = toLocalDateString(currentDate);
      
      // Build request body based on view mode
      // Include browser timezone for accurate timestamp formatting in exports
      const requestBody: {
        format: string;
        viewMode: string;
        workspace_id?: string;
        my_events?: boolean;
        date?: string;
        start_date?: string;
        end_date?: string;
        timezone?: string;
      } = {
        format,
        viewMode: displayMode === 'cards' ? 'card' : 'table',
        workspace_id: workspaceId,
        my_events: !workspaceId && isMyEvents ? true : undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      if (viewMode === 'day') {
        requestBody.date = dayDate;
      } else {
        requestBody.start_date = rangeStartDate;
        requestBody.end_date = rangeEndDate;
      }
      
      const response = await api.post('/calendar/export', requestBody, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header if available
      const disposition = response.headers['content-disposition'];
      let filename = `calendar-${viewMode}-${dayDate}.${format === 'word' ? 'docx' : format === 'excel' ? 'xlsx' : format}`;
      
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename=([^;]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Calendar exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export calendar');
    }
  };
  
  // Simple ref to prevent multiple fetches
  const lastFetchRef = useRef<string>('');

  // Workspace event create/edit modal (PRD §5.2)
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<CalendarEvent | null>(null);
  
  const { 
    events, 
    isLoading, 
    error, 
    fetchCalendarData
  } = useCalendar(workspaceId, isMyEvents, sources);

  // Separate fetch for the date-strip event dots (esp. when in day/week view).
  // Anchored a week before the selected date so the strip's visible window
  // (roughly selected −7 to +22 days) has dot data regardless of view mode.
  // Same sources as the main fetch — a dot must never promise a day whose
  // events this page then doesn't show (PRD acceptance 13.4).
  const lastDotsFetchRef = useRef<string>('');
  const {
    events: dotEvents,
    fetchCalendarData: fetchDotEvents,
  } = useCalendar(workspaceId, isMyEvents, sources);

  // Formatted date range string for the date indicator
  const formattedDateRange = useMemo(() => {
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + (viewMode === 'month' ? 29 : 6));
    const startStr = currentDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return `${startStr} – ${endStr}`;
  }, [currentDate, viewMode]);

  // Check if current date is today
  const isToday = useMemo(() => {
    const today = new Date();
    return currentDate.toDateString() === today.toDateString();
  }, [currentDate]);

  // Simple fetch on mount and when URL changes
  // Smart default (§5.7): with no URL param and no stored choice, the first
  // fetch ran as All Events; flip to My Events when the window holds anything
  // of the user's. Never persisted — only a click is a choice.
  useEffect(() => {
    if (workspaceId || urlFilter || storedScope || derivedScope !== null) return;
    if (!events || events.length === 0) return;
    setDerivedScope(deriveDefaultScope(events));
  }, [workspaceId, urlFilter, storedScope, derivedScope, events]);

  useEffect(() => {
    const fetchKey = `${workspaceId || 'all'}-${currentYear}-${currentMonth}-${currentDay}-${viewMode}-${eventFilter}`;
    
    // Only fetch if we haven't fetched this combination before
    if (lastFetchRef.current !== fetchKey) {
      lastFetchRef.current = fetchKey;
      fetchCalendarData(currentDate, viewMode);
    }
  }, [workspaceId, currentYear, currentMonth, currentDay, viewMode, eventFilter, fetchCalendarData, currentDate]);

  useEffect(() => {
    const dotsAnchor = new Date(currentYear, currentMonth - 1, currentDay - 7);
    const dotsKey = `${workspaceId || 'all'}-${toLocalDateString(dotsAnchor)}-${eventFilter}`;
    if (lastDotsFetchRef.current !== dotsKey) {
      lastDotsFetchRef.current = dotsKey;
      fetchDotEvents(dotsAnchor, 'month');
    }
  }, [workspaceId, currentYear, currentMonth, currentDay, fetchDotEvents, eventFilter]);

  // Navigation: shift anchor date by view-appropriate number of days
  const navigateByDays = (days: number) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + days);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('year', newDate.getFullYear().toString());
    newSearchParams.set('month', (newDate.getMonth() + 1).toString());
    newSearchParams.set('day', newDate.getDate().toString());
    setSearchParams(newSearchParams);
  };

  const goToPrevious = () => {
    if (viewMode === 'month') navigateByDays(-30);
    else if (viewMode === 'week') navigateByDays(-7);
    else navigateByDays(-1);
  };

  const goToNext = () => {
    if (viewMode === 'month') navigateByDays(30);
    else if (viewMode === 'week') navigateByDays(7);
    else navigateByDays(1);
  };

  const goToToday = () => {
    const today = new Date();
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('year', today.getFullYear().toString());
    newSearchParams.set('month', (today.getMonth() + 1).toString());
    newSearchParams.set('day', today.getDate().toString());
    setSearchParams(newSearchParams);
  };


  const setViewMode = (mode: ViewMode) => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('view', mode);
    setSearchParams(newSearchParams);
  };

  const eventCountsByDate = useMemo(() => {
    const counts = new Map<string, number>();
    dotEvents.forEach((evt: any) => {
      const dateOnly = evt.reading_date ? evt.reading_date.split('T')[0] : null;
      if (dateOnly) counts.set(dateOnly, (counts.get(dateOnly) || 0) + 1);
    });
    return counts;
  }, [dotEvents]);

  const onSelectDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split('-').map((v) => parseInt(v, 10));
    if (!y || !m || !d) return;

    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('year', y.toString());
    newSearchParams.set('month', m.toString());
    newSearchParams.set('day', d.toString());
    setSearchParams(newSearchParams);
  };

  // Filter events based on search and chamber filter
  const filteredEvents = useMemo(() => {
    if (!events || !Array.isArray(events)) return [];
    
    return events.filter((event: any) => {
      // Chamber filter
      if (chamberFilter !== 'all') {
        if (chamberFilter === 'joint') {
          if (!event.joint_meeting && event.chamber?.toLowerCase() !== 'joint') {
            return false;
          }
        } else {
          if (event.chamber?.toLowerCase() !== chamberFilter) {
            return false;
          }
        }
      }
      
      // Search filter
      if (searchQuery.trim()) {
        const searchLower = searchQuery.toLowerCase();
        const searchableText = [
          event.reading_title,
          event.calendar_description,
          event.room_number,
          event.chamber,
          ...(event.bill_numbers || [])
        ].filter(Boolean).join(' ').toLowerCase();
        
        if (!searchableText.includes(searchLower)) {
          return false;
        }
      }
      
      return true;
    });
  }, [events, searchQuery, chamberFilter]);

  return (
    <div className="space-y-6">
      {/* Header. As a page (hideHeading) the global header carries the title and
          help trigger, so the controls take the row and sit left; inside a
          workspace tab the in-body heading keeps the left slot. */}
      <div className={`flex flex-col sm:flex-row sm:items-start gap-4 ${hideHeading ? '' : 'sm:justify-between'}`}>
        {!hideHeading && (
          <div>
            <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary flex items-center gap-2">
              Legislative Calendar
              {/* Hide help icon on mobile */}
              <span className="hidden md:inline-flex">
                <HelpTrigger contentKey="workspace-calendar" size="md" />
              </span>
            </h2>
            {/* Hide description on mobile */}
            <p className="hidden md:block text-text-secondary dark:text-text-dark-secondary">
              Track upcoming legislative events and hearings
            </p>
          </div>
        )}

        {/* Action/filter controls */}
        <div className="flex flex-wrap items-center gap-3">
          {/* My Events / All Events toggle - only on general calendar */}
          {!workspaceId && (
            <div className="inline-flex rounded-lg border border-border-light dark:border-border-dark overflow-hidden text-sm">
              <button
                onClick={() => setEventFilter('my_events')}
                className={`px-3 py-1.5 transition-colors ${
                  isMyEvents
                    ? 'bg-primary-600 text-white'
                    : 'text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                }`}
              >
                My Events
              </button>
              <button
                onClick={() => setEventFilter('all_events')}
                className={`px-3 py-1.5 transition-colors ${
                  !isMyEvents
                    ? 'bg-primary-600 text-white'
                    : 'text-text-secondary dark:text-text-dark-secondary hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary'
                }`}
              >
                All Events
              </button>
            </div>
          )}

          {/* Duration selector */}
          <SegmentedControl
            ariaLabel="Calendar duration"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: 'month', label: '30 Day' },
              { id: 'week', label: '7 Day' },
              { id: 'day', label: '1 Day' },
            ]}
          />

          {/* Add workspace event (members with write access; server enforces) */}
          {workspaceId && (
            <Button
              variant="secondary"
              size="sm"
              icon={Plus}
              onClick={() => { setEditingEvent(null); setEventModalOpen(true); }}
            >
              Add Event
            </Button>
          )}

          {/* Share */}
          <ShareCalendarDropdown
            date={rangeStartDate}
            workspaceId={workspaceId}
            organizationId={organizationId}
            rangeType={viewMode === 'day' ? undefined : viewMode}
            rangeStartDate={viewMode !== 'day' ? rangeStartDate : undefined}
            eventFilter={!workspaceId ? eventFilter as 'my_events' | 'all_events' : undefined}
          />

          <ExportMenu
            variant="primary"
            formats={
              effectiveDisplayMode === 'table'
                ? ['csv', 'excel', { format: 'pdf', note: 'Landscape' }]
                : [{ format: 'word', note: 'Portrait' }, { format: 'pdf', note: 'Portrait' }]
            }
            onExport={handleExport}
          />

          {/* Chamber filter */}
          <select
            value={chamberFilter}
            onChange={(e) => setChamberFilter(e.target.value as 'all' | 'senate' | 'house' | 'joint')}
            className="px-3 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
          >
            <option value="all">All Chambers</option>
            <option value="senate">Senate</option>
            <option value="house">House</option>
            <option value="joint">Joint</option>
          </select>

          {/* Search */}
          <SearchField
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search events..."
            size="sm"
            className="w-40"
          />

        </div>
      </div>

      {/* Content: date strip on top + agenda/table at full width */}
      <div className="space-y-4">
        {/* Card/table toggle beside the date selector, which defaults to (and
            auto-centers on) the current date */}
        <div className="flex items-center gap-3">
          <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
            <ViewToggle
              value={displayMode}
              onChange={setDisplayMode}
              options={[
                { id: 'cards', label: 'Card View includes all events', icon: LayoutGrid },
                { id: 'table', label: 'List View includes only bill related events', icon: List },
              ]}
            />
          </div>
          {/* Same date-jump popover as the Pro home My Calendar panel */}
          <DatePickerPopover
            selectedDate={toLocalDateString(currentDate)}
            onSelectDate={onSelectDate}
            hasEvent={(dateStr) => (eventCountsByDate.get(dateStr) || 0) > 0}
            className="flex-shrink-0"
          />
          <HorizontalDateStrip
            className="flex-1 min-w-0"
            selectedDate={toLocalDateString(currentDate)}
            onSelectDate={onSelectDate}
            rangeStart={viewMode !== 'day' ? rangeStartDate : undefined}
            rangeEnd={viewMode !== 'day' ? rangeEndDate : undefined}
            eventCountForDate={(dateStr) => eventCountsByDate.get(dateStr) || 0}
          />
        </div>

        <div className="space-y-4">
          {/* Date indicator with prev/next navigation */}
          <div className="flex items-center gap-2 text-sm text-text-tertiary dark:text-text-dark-tertiary">
            <button
              onClick={goToPrevious}
              className="p-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
              title={`Previous ${viewMode === 'month' ? '30 days' : viewMode === 'week' ? '7 days' : 'day'}`}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="font-medium text-text-primary dark:text-text-dark-primary">
              {formattedDateRange}
            </span>
            <button
              onClick={goToNext}
              className="p-1 hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary rounded transition-colors"
              title={`Next ${viewMode === 'month' ? '30 days' : viewMode === 'week' ? '7 days' : 'day'}`}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            {!isToday && (
              <button
                onClick={goToToday}
                className="ml-2 px-2 py-0.5 text-xs bg-background-tertiary dark:bg-background-dark-tertiary rounded hover:bg-neutral-200 dark:hover:bg-background-dark-quaternary transition-colors"
              >
                Today
              </button>
            )}
          </div>

          {/* Workspace events in the window — distinct from legislative rows,
              with edit access (author/owner enforced server-side). Full badge
              integration inside the shared agenda views lands with the
              source-filter surfaces slice. */}
          {workspaceId && events.some((e: any) => e.event_source === 'workspace') && (
            <div className="mb-3 rounded-lg border border-border-light dark:border-border-dark-hairline bg-neutral-50 dark:bg-background-dark-tertiary px-3 py-2">
              <div className="text-[10px] font-bold uppercase tracking-[.12em] text-primary-500 dark:text-primary-dark-500 mb-1.5">
                Workspace events
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(events as any[])
                  .filter((e) => e.event_source === 'workspace')
                  .map((e) => (
                    <button
                      key={e.event_key}
                      type="button"
                      onClick={() => { setEditingEvent(e); setEventModalOpen(true); }}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border-light dark:border-border-dark-hairline bg-white dark:bg-background-dark-secondary px-2 py-1 text-xs text-text-primary dark:text-text-dark-primary hover:border-primary-300"
                      title="Edit event"
                    >
                      <span className="font-semibold">{String(e.reading_date || '').slice(5, 10)}</span>
                      <span className="truncate max-w-[220px]">{e.reading_title}</span>
                      <EditIcon className="w-3 h-3 text-slate-400" aria-hidden="true" />
                    </button>
                  ))}
              </div>
            </div>
          )}

          {effectiveDisplayMode === 'cards' ? (
            <CalendarAgendaView
              events={filteredEvents as any}
              isPublic={false}
              workspaceId={workspaceId}
              onBillClick={(billNumber) => {
                if (workspaceId) {
                  navigate(`/legislation/${encodeURIComponent(billNumber)}?workspace=${workspaceId}&returnTo=workspace`);
                } else {
                  navigate(`/legislation/${encodeURIComponent(billNumber)}`);
                }
              }}
            />
          ) : (
            <div className="mt-4">
              <CalendarDayView
                events={filteredEvents as any}
                viewMode="table"
                isPublic={false}
                workspaceId={workspaceId}
                showDateColumn={viewMode !== 'day'}
                onBillClick={(billNumber) => {
                  if (workspaceId) {
                    navigate(`/legislation/${encodeURIComponent(billNumber)}?workspace=${workspaceId}&returnTo=workspace`);
                  } else {
                    navigate(`/legislation/${encodeURIComponent(billNumber)}`);
                  }
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="text-center py-12">
          <LoadingSpinner size="lg" className="mx-auto" />
          <p className="mt-2 text-text-secondary dark:text-text-dark-secondary">Loading calendar...</p>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="card p-4">
          <div className="flex items-start">
            <CalendarIcon className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                Error Loading Calendar
              </h4>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary mt-1">
                {error}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Empty state - no events for workspace */}
      {!isLoading && !error && filteredEvents.length === 0 && (
        <div className="card p-12">
          <div className="text-center">
            <CalendarIcon className="w-16 h-16 text-text-tertiary dark:text-text-dark-tertiary mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary mb-2">
              {(searchQuery.trim() || chamberFilter !== 'all') ? 'No Matching Events' : 'No Calendar Events'}
            </h3>
            <p className="text-text-secondary dark:text-text-dark-secondary max-w-md mx-auto">
              {(searchQuery.trim() || chamberFilter !== 'all') 
                ? `No events found${searchQuery.trim() ? ` matching "${searchQuery.trim()}"` : ''}${chamberFilter !== 'all' ? ` in ${chamberFilter} chamber` : ''}.`
                : 'There are no upcoming calendar events. Events will appear here when bills are scheduled for hearings or readings.'}
            </p>
          </div>
        </div>
      )}
      {workspaceId && (
        <WorkspaceEventModal
          workspaceId={workspaceId}
          event={editingEvent}
          isOpen={eventModalOpen}
          onClose={() => setEventModalOpen(false)}
          onSaved={() => {
            lastFetchRef.current = '';
            lastDotsFetchRef.current = '';
            fetchCalendarData(currentDate, viewMode);
          }}
        />
      )}
    </div>
  );
};

export default CalendarTab;